import type { UseMutateFunction } from '@tanstack/react-query';
import type { FC, ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext } from 'react';

import { CHIRP_APP_URL, COGNITO_CLIENT_ID, COGNITO_URL } from 'config';
import type { GetUserAuthTokensResponseDTO } from 'declarations/dto/auth';
import { StorageE } from 'declarations/enums/storage';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { useFetchUserAuthTokens } from 'hooks/api/useAuth';
import useAuthStore from 'hooks/store/useAuthStore';

export type AuthContextT = {
  isUserLoggedIn: boolean;
  login: UseMutateFunction<GetUserAuthTokensResponseDTO, ApiErrorI, string>;
  isLoginLoading: boolean;
  signOut: NoopT;
};

type AuthProviderPropsT = { children: ReactNode };

const contextDefaultValue = {
  isUserLoggedIn: false,
  isLoginLoading: false,
  login: (): Promise<null> => Promise.resolve(null),
  signOut: (): void => undefined,
};

export const AuthContext = createContext<AuthContextT>(contextDefaultValue);

const AuthProvider: FC<AuthProviderPropsT> = ({ children }): ReactElement => {
  /**
   * Hooks
   */
  const { isUserLoggedIn, setUserLoginStatus } = useAuthStore(state => state);

  /**
   * Api hooks
   */
  const createTokensMutation = useFetchUserAuthTokens({
    onSuccess: (data: GetUserAuthTokensResponseDTO): void => {
      localStorage.setItem(StorageE.IdToken, data.id_token);
      localStorage.setItem(StorageE.RefreshToken, data.refresh_token);
      setUserLoginStatus(true);
    },
  });

  /**
   * Handlers
   */
  const signOut = useCallback((): void => {
    window.location.href = `${COGNITO_URL}/logout?client_id=${COGNITO_CLIENT_ID}&logout_uri=${CHIRP_APP_URL}/logout`;
    localStorage.removeItem(StorageE.IdToken);
    localStorage.removeItem(StorageE.RefreshToken);
    setUserLoginStatus(false);
  }, [setUserLoginStatus]);

  return (
    <AuthContext.Provider
      value={{
        isUserLoggedIn,
        login: createTokensMutation.mutate,
        isLoginLoading: createTokensMutation.isLoading,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = (): AuthContextT => useContext(AuthContext);

export { AuthProvider, useAuth };
